import { Transaction } from '@/core/models/interface';
import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class Timer {
    // timer
    timerDisplay: string = '0:05:00';
    isTimerRunning: boolean = false;
    private timerInterval: any;
    private remainingTime: number = 300; // 5 minutes in seconds

    startTimer() {
        if (this.isTimerRunning) return;

        this.isTimerRunning = true;
        this.remainingTime = 300; // Reset to 5 minutes

        this.timerInterval = setInterval(() => {
            this.remainingTime--;

            // Calculate minutes and seconds
            const minutes = Math.floor(this.remainingTime / 60);
            const seconds = this.remainingTime % 60;

            // Format as 0:MM:SS
            this.timerDisplay = `0:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

            // Stop timer when it reaches 0
            if (this.remainingTime <= 0) {
                this.stopTimer();
            }
        }, 1000);
    }

    stopTimer() {
        clearInterval(this.timerInterval);
        this.isTimerRunning = false;
        this.timerDisplay = '0:05:00';
    }

    ngOnDestroy() {
        this.stopTimer();
    }
}

// Helper function to generate initials from a name
export function generateInitials(name: string): string {
    if (!name) return '??';
    const nameParts = name.trim().split(' ');
    const initials = nameParts
        .filter((part) => part.length > 0)
        .map((part) => part[0].toUpperCase())
        .join('')
        .slice(0, 2);
    return initials || '??';
}

// Helper function to generate custom timestamp
export function getCustomTimestamp(): string {
    const now = new Date();
    const year = now.getFullYear();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}${day}${month}${hours}${minutes}${seconds}`;
}

// Enum to get FileTypes
export enum FileTypes {
    payee = 'payee',
    transaction = 'transaction',
    report = 'report',
    data = 'data',
    document = 'document',
    prospectDocument = 'prospect_document'
}

// contract.constants.ts
export const CONTRACT_FILE_NAME = 'Contrato.pdf';
export const TERMS_FILE_NAME = 'tyc_eplata.pdf';

export const CONTRACT_DATE_FORMAT = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
} as const;

export const CONTRACT_PDF_OPTIONS = {
    margin: 10,
    filename: CONTRACT_FILE_NAME,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
} as const;

export const TERMS_PDF_OPTIONS = {
    margin: 10,
    filename: TERMS_FILE_NAME,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
} as const;

// Mapping of tab keys to display names for dynamic dialog messages
export const tabDisplayNames: { [key: string]: string } = {
    generalData: 'Datos Generales',
    transactionality: 'Transaccionalidad',
    economicActivity: 'Actividad Económica',
    bankingInfoForFunding: 'Información Bancaria para Financiamiento',
    politicallyExposedPersons: 'Personas Políticamente Expuestas',
    beneficiaries: 'Beneficiarios',
    legals: 'Legales',
    testimonyLegalRepresentative: 'Testimonio de Otorgamiento de Poderes del Representante Legal',
    legalRepresentative: 'Representante Legal',
    ownershipStructure: 'Estructura Accionaria',
    organizationalStructure: 'Estructura Organizacional',
    accessPermissions: 'Permisos de Acceso'
};

export const getIDCategory: any = (search: string) => {
    const serviceCategories: any = {
        servicios: [5, 15],
        'impuestos / gobierno': [7],
        'productos financieros': [8],
        'productos por catálogo': [9],
        'servicios de prepago': [10],
        entretenimiento: [11],
        'otras recargas': [13],
        transporte: [18],
        'internet de prepago': [19]
    };

    return serviceCategories[search.toLowerCase()];
};

export function isVatOrFee(transaction: Transaction): boolean {
    // Placeholder: Adjust based on actual logic (e.g., check memo for 'VAT' or 'FEE')
    return transaction.memo ? transaction.memo.toLowerCase().includes('vat') || transaction.memo.toLowerCase().includes('fee') : false;
}

export function isTransferOut(transaction: Transaction): boolean {
    return transaction.amount < 0;
}

export function vatOrFeeText(transaction: Transaction): string {
    return isVatOrFee(transaction) ? 'VAT/Fee' : transaction.memo || 'N/A';
}

export enum StorageKeys {
    AUTH_USER = 'auth_user',
    SESSION = 'session',
    FORCE_LOGOUT = 'force_logout'
}

export enum UserTypes {
    PROSPECT = 'prospect',
    CUSTOMER = 'customer'
}

export enum ChallengeNames {
    MFA_SETUP = 'MFA_SETUP',
    SOFTWARE_TOKEN_MFA = 'SOFTWARE_TOKEN_MFA'
}

export const ERROR_MESSAGES = {
    REQUIRED_CODE: 'Se requiere código',
    INVALID_CODE_LENGTH: 'El código debe tener al menos 6 caracteres',
    REQUIRED_EMAIL: 'Correo Electrónico requerido'
};
